/**
 * Roofs for any footprint: a weighted straight skeleton (pure; twin of app/design/sketch/skeleton.py, tested in
 * footprint.test.ts against the shared cases in apps/solar-backend/tests/fixtures/sketch_skeleton.json).
 *
 * Every footprint side is an eave: `hip` (a face rises from it at its pitch) or `gable` (a vertical gable end, only
 * on a side between two outside corners). A side with pitch p moves inwards 1 / tan(p) metres per metre of rise, a
 * gable side stands still, so the skeleton's time is the height above the eaves. Collinear sides are merged first
 * (the longer piece's settings win); a vertex between two opposite parallel sides stands still (a ridge's end); a
 * wavefront loop whose area reaches zero has finished (its sides are ridges). Events come from the vertices'
 * straight paths, earliest first; ties go to edge events, then to the lower vertex number, exactly as the server.
 *
 * `footprintLines` turns the result into Sketch lines (eaves, rakes, ridges, hips, valleys) for skeleton.ts.
 */
import type { Vec2 } from "../editor/geo.ts";

export type EdgeMode = "hip" | "gable";
export interface EdgeSpec {
  mode: EdgeMode;
  pitch: number;
}
export type Vec3 = [number, number, number];
export interface SkeletonFace {
  ring: number;
  side: number;
  pitch: number;
  /** Counter-clockwise from above: x, y, height above the eaves. */
  points: Vec3[];
}
export interface SkeletonLine {
  a: Vec3;
  b: Vec3;
  type: "eave" | "rake" | "ridge" | "hip" | "valley";
}
export interface SkeletonRoof {
  faces: SkeletonFace[];
  lines: SkeletonLine[];
}

const EPS = 1e-7;
const POINT_TOL = 1e-5;
const AREA_TOL = 1e-6;
const COLLINEAR_SIN = Math.sin((0.1 * Math.PI) / 180);
export const MIN_PITCH = 3;
export const MAX_PITCH = 75;

const mod = (a: number, n: number) => ((a % n) + n) % n;
const sub = (a: Vec2, b: Vec2): Vec2 => [a[0] - b[0], a[1] - b[1]];
const dot = (a: Vec2, b: Vec2) => a[0] * b[0] + a[1] * b[1];
const cross = (a: Vec2, b: Vec2) => a[0] * b[1] - a[1] * b[0];
const norm = (a: Vec2) => Math.hypot(a[0], a[1]);
const round6 = (v: number) => Math.round(v * 1e6) / 1e6;

export function ringArea(points: ArrayLike<number>[]): number {
  let total = 0;
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    const q = points[(i + 1) % points.length]!;
    total += p[0]! * q[1]! - q[0]! * p[1]!;
  }
  return total / 2;
}

interface Edge {
  index: number;
  ring: number;
  side: number;
  start: Vec2;
  end: Vec2;
  direction: Vec2;
  normal: Vec2;
  offset: number;
  weight: number;
  pitch: number;
  gable: boolean;
}

interface Vertex {
  id: number;
  left: number;
  right: number;
  origin: Vec2;
  t0: number;
  velocity: Vec2;
  node: number;
  reflex: boolean;
  prev: number;
  next: number;
  active: boolean;
}

const at = (v: Vertex, t: number): Vec2 => [v.origin[0] + v.velocity[0] * (t - v.t0), v.origin[1] + v.velocity[1] * (t - v.t0)];

type Arc = [number, number, number, string];
type EventKey = [number, number, number, number];

function before(a: EventKey, b: EventKey): boolean {
  if (Math.abs(a[0] - b[0]) > 1e-9) return a[0] < b[0];
  for (let i = 1; i < 4; i++) if (a[i] !== b[i]) return a[i]! < b[i]!;
  return false;
}

class Solver {
  vertices: Vertex[] = [];
  nodes: Vec3[] = [];
  arcs: Arc[] = [];
  t = 0;
  edges: Edge[];
  constructor(edges: Edge[]) {
    this.edges = edges;
  }

  node(p: Vec2, t: number): number {
    for (let i = 0; i < this.nodes.length; i++) {
      const [x, y] = this.nodes[i]!;
      if (Math.abs(x - p[0]) <= POINT_TOL && Math.abs(y - p[1]) <= POINT_TOL) return i;
    }
    this.nodes.push([p[0], p[1], t]);
    return this.nodes.length - 1;
  }

  velocity(left: number, right: number): Vec2 {
    const a = this.edges[left]!;
    const b = this.edges[right]!;
    const det = cross(a.normal, b.normal);
    if (Math.abs(det) < 1e-9) {
      if (dot(a.normal, b.normal) > 0) {
        const w = (a.weight + b.weight) / 2;
        return [a.normal[0] * w, a.normal[1] * w];
      }
      return [0, 0];
    }
    return [(a.weight * b.normal[1] - b.weight * a.normal[1]) / det, (a.normal[0] * b.weight - b.normal[0] * a.weight) / det];
  }

  vertex(left: number, right: number, p: Vec2, t: number, node: number): Vertex {
    const reflex = cross(this.edges[left]!.direction, this.edges[right]!.direction) < -1e-12;
    const v: Vertex = { id: this.vertices.length, left, right, origin: p, t0: t, velocity: this.velocity(left, right), node, reflex, prev: -1, next: -1, active: true };
    this.vertices.push(v);
    return v;
  }

  arc(a: number, b: number, face: number, kind: string) {
    if (a !== b) this.arcs.push([a, b, face, kind]);
  }

  loop(start: Vertex): Vertex[] {
    const out = [start];
    let v = this.vertices[start.next]!;
    let guard = 0;
    while (v.id !== start.id && guard++ < 100000) {
      out.push(v);
      v = this.vertices[v.next]!;
    }
    return out;
  }

  edgeEvent(a: Vertex, b: Vertex): number | null {
    const pa = at(a, this.t);
    const pb = at(b, this.t);
    const d0 = sub(pb, pa);
    if (norm(d0) <= POINT_TOL) return this.t;
    const dv = sub(b.velocity, a.velocity);
    const speed2 = dot(dv, dv);
    if (speed2 < 1e-18) return null;
    const dt = -dot(d0, dv) / speed2;
    if (dt < -EPS) return null;
    const gap: Vec2 = [d0[0] + dv[0] * dt, d0[1] + dv[1] * dt];
    if (norm(gap) > 1e-6 * Math.max(1, norm(d0))) return null;
    return this.t + Math.max(dt, 0);
  }

  splitEvent(v: Vertex, u: Vertex): [number, Vec2] | null {
    const e = this.edges[u.right]!;
    const p = at(v, this.t);
    const gap = dot(e.normal, p) - (e.offset + e.weight * this.t);
    const k = dot(e.normal, v.velocity) - e.weight;
    if (gap < -1e-6 || k > -1e-12) return null;
    const t = this.t + gap / -k;
    const x = at(v, t);
    const w = this.vertices[u.next]!;
    const pu = at(u, t);
    const pw = at(w, t);
    const s = dot(e.direction, sub(x, pu));
    const length = dot(e.direction, sub(pw, pu));
    if (s < -1e-6 || s > length + 1e-6) return null;
    return [t, x];
  }

  nextEvent(): { key: EventKey; kind: "edge" | "split"; a: Vertex; b: Vertex; x: Vec2 | null } | null {
    let best: { key: EventKey; kind: "edge" | "split"; a: Vertex; b: Vertex; x: Vec2 | null } | null = null;
    const active = this.vertices.filter((v) => v.active);
    for (const a of active) {
      const b = this.vertices[a.next]!;
      if (b.id === a.id) continue;
      const t = this.edgeEvent(a, b);
      if (t !== null) {
        const key: EventKey = [t, 0, a.id, b.id];
        if (!best || before(key, best.key)) best = { key, kind: "edge", a, b, x: null };
      }
    }
    for (const v of active) {
      if (!v.reflex) continue;
      for (const u of active) {
        const w = this.vertices[u.next]!;
        if (u.id === v.id || u.id === v.prev || w.id === v.id || u.right === v.left || u.right === v.right) continue;
        const hit = this.splitEvent(v, u);
        if (hit) {
          const key: EventKey = [hit[0], 1, v.id, u.id];
          if (!best || before(key, best.key)) best = { key, kind: "split", a: v, b: u, x: hit[1] };
        }
      }
    }
    return best;
  }

  finish(v: Vertex, node: number) {
    const kind = v.reflex ? "reflex" : "convex";
    this.arc(v.node, node, v.left, kind);
    this.arc(v.node, node, v.right, kind);
    v.active = false;
  }

  doEdge(a: Vertex, b: Vertex): Vertex[] {
    const p = at(a, this.t);
    const q = at(b, this.t);
    const x: Vec2 = [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2];
    const node = this.node(x, this.t);
    const prev = a.prev;
    const next = b.next;
    this.finish(a, node);
    this.finish(b, node);
    if (prev === b.id) return [];
    const v = this.vertex(a.left, b.right, x, this.t, node);
    v.prev = prev;
    v.next = next;
    this.vertices[prev]!.next = v.id;
    this.vertices[next]!.prev = v.id;
    return [v];
  }

  doSplit(v: Vertex, u: Vertex, x: Vec2): Vertex[] {
    const node = this.node(x, this.t);
    const e = u.right;
    const w = this.vertices[u.next]!;
    const prev = v.prev;
    const next = v.next;
    this.finish(v, node);
    const v1 = this.vertex(v.left, e, x, this.t, node);
    const v2 = this.vertex(e, v.right, x, this.t, node);
    v1.prev = prev;
    v1.next = w.id;
    this.vertices[prev]!.next = v1.id;
    w.prev = v1.id;
    v2.prev = u.id;
    v2.next = next;
    u.next = v2.id;
    this.vertices[next]!.prev = v2.id;
    return [v1, v2];
  }

  collapseIfFlat(v: Vertex) {
    if (!v.active) return;
    const loop = this.loop(v);
    const points = loop.map((x) => at(x, this.t));
    if (loop.length > 2 && Math.abs(ringArea(points)) > AREA_TOL) return;
    this.collapse(loop);
  }

  collapse(loop: Vertex[]) {
    const ends = new Map<number, number>();
    for (const x of loop) {
      const node = this.node(at(x, this.t), this.t);
      ends.set(x.id, node);
      this.finish(x, node);
    }
    for (const x of loop) this.arc(ends.get(x.id)!, ends.get(x.next)!, x.right, "ridge");
  }

  run() {
    let guard = 0;
    const limit = 20 * this.vertices.length + 50;
    while (this.vertices.some((v) => v.active) && guard++ < limit) {
      const event = this.nextEvent();
      if (!event) {
        for (const v of this.vertices) if (v.active) this.collapse(this.loop(v));
        break;
      }
      this.t = Math.max(this.t, event.key[0]);
      const created = event.kind === "edge" ? this.doEdge(event.a, event.b) : this.doSplit(event.a, event.b, event.x!);
      for (const v of created) this.collapseIfFlat(v);
    }
  }
}

function convex(points: Vec2[], i: number): boolean {
  const n = points.length;
  const a = points[mod(i - 1, n)]!;
  const b = points[i]!;
  const c = points[mod(i + 1, n)]!;
  return cross(sub(b, a), sub(c, b)) > 1e-12;
}

/** Whether side `side` of a footprint (any orientation) may be a gable end: both its corners are outside ones. */
export function gableAllowed(points: Vec2[], side: number): boolean {
  let pts = points.map((p) => [p[0], p[1]] as Vec2);
  if (ringArea(pts) < 0) {
    const n = pts.length;
    pts = pts.reverse();
    side = mod(n - 2 - side, n);
  }
  return convex(pts, side) && convex(pts, mod(side + 1, pts.length));
}

function cleanRing(points: Vec2[], specsIn: EdgeSpec[], outer: boolean): { pts: Vec2[]; specs: EdgeSpec[]; sides: number[] } {
  let pts = points.map((p) => [Number(p[0]), Number(p[1])] as Vec2);
  let sides = pts.map((_, i) => i);
  let specs = pts.map((_, i) => specsIn[i] ?? { mode: "hip" as EdgeMode, pitch: 30 });
  if (ringArea(pts) < 0 === outer) {
    const n = pts.length;
    pts = pts.reverse();
    specs = pts.map((_, i) => specs[mod(n - 2 - i, n)]!);
    sides = pts.map((_, i) => mod(n - 2 - i, n));
  }
  let changed = true;
  while (changed && pts.length >= 3) {
    changed = false;
    let n = pts.length;
    for (let i = 0; i < n; i++) {
      if (norm(sub(pts[(i + 1) % n]!, pts[i]!)) < 1e-4) {
        pts.splice(i, 1);
        specs.splice(i, 1);
        sides.splice(i, 1);
        changed = true;
        break;
      }
    }
    if (changed) continue;
    n = pts.length;
    for (let i = 0; i < n; i++) {
      const a = pts[mod(i - 1, n)]!;
      const b = pts[i]!;
      const c = pts[(i + 1) % n]!;
      const u = sub(b, a);
      const v = sub(c, b);
      if (Math.abs(cross(u, v)) <= COLLINEAR_SIN * norm(u) * norm(v) && dot(u, v) > 0) {
        const j = mod(i - 1, n);
        if (norm(v) > norm(u)) {
          specs[j] = specs[i]!;
          sides[j] = sides[i]!;
        }
        pts.splice(i, 1);
        specs.splice(i, 1);
        sides.splice(i, 1);
        changed = true;
        break;
      }
    }
  }
  return { pts, specs, sides };
}

/** The roof over a footprint: `rings[0]` the outline, the others holes (courtyards); `specs[r][i]` the side from
 * `rings[r][i]` to the next point. Heights are above the eaves. */
export function straightSkeletonRoof(rings: Vec2[][], specs: EdgeSpec[][]): SkeletonRoof {
  if (!rings.length || rings[0]!.length < 3) throw new Error("a footprint needs at least three corners");
  const edges: Edge[] = [];
  const cleaned: [number, number][] = [];
  rings.forEach((ring, r) => {
    const { pts, specs: sp, sides } = cleanRing(ring, specs[r] ?? [], r === 0);
    if (pts.length < 3) {
      if (r === 0) throw new Error("the footprint has no area");
      return;
    }
    const first = edges.length;
    pts.forEach((p, i) => {
      const q = pts[(i + 1) % pts.length]!;
      const d = sub(q, p);
      const length = norm(d);
      const direction: Vec2 = [d[0] / length, d[1] / length];
      const normal: Vec2 = [-direction[1], direction[0]];
      const gable = sp[i]!.mode === "gable" && convex(pts, i) && convex(pts, (i + 1) % pts.length);
      const pitch = Math.min(MAX_PITCH, Math.max(MIN_PITCH, Number(sp[i]!.pitch)));
      const weight = gable ? 0 : 1 / Math.tan((pitch * Math.PI) / 180);
      edges.push({ index: edges.length, ring: r, side: sides[i]!, start: p, end: q, direction, normal, offset: dot(normal, p), weight, pitch, gable });
    });
    cleaned.push([first, pts.length]);
  });
  const solver = new Solver(edges);
  for (const [first, count] of cleaned) {
    const ids: number[] = [];
    for (let i = 0; i < count; i++) {
      const eIn = edges[first + mod(i - 1, count)]!;
      const eOut = edges[first + i]!;
      ids.push(solver.vertex(eIn.index, eOut.index, eOut.start, 0, solver.node(eOut.start, 0)).id);
    }
    ids.forEach((id, i) => {
      solver.vertices[id]!.prev = ids[mod(i - 1, count)]!;
      solver.vertices[id]!.next = ids[(i + 1) % count]!;
    });
  }
  solver.run();
  return assemble(solver, edges);
}

function height(edge: Edge, p: Vec3): number {
  if (edge.gable) return p[2];
  return Math.max(0, dot(edge.normal, [p[0], p[1]]) - edge.offset) / edge.weight;
}

function assemble(solver: Solver, edges: Edge[]): SkeletonRoof {
  const nodes = solver.nodes;
  const start = new Map(edges.map((e) => [e.index, solver.node(e.start, 0)]));
  const end = new Map(edges.map((e) => [e.index, solver.node(e.end, 0)]));
  const key = (a: number, b: number) => (a < b ? `${a}|${b}` : `${b}|${a}`);
  const byFace = new Map<number, Map<string, string>>(edges.map((e) => [e.index, new Map()]));
  for (const [a, b, face, kind] of solver.arcs) {
    if (a === b) continue;
    const pieces = byFace.get(face)!;
    if (!pieces.has(key(a, b))) pieces.set(key(a, b), kind);
  }
  const roof: SkeletonRoof = { faces: [], lines: [] };
  for (const e of edges) {
    if (e.gable) continue;
    const ring = walk(nodes, byFace.get(e.index)!, start.get(e.index)!, end.get(e.index)!);
    if (!ring) continue;
    const points = ring.map((n) => [nodes[n]![0], nodes[n]![1], height(e, nodes[n]!)] as Vec3);
    if (ringArea(points) <= AREA_TOL) continue;
    roof.faces.push({ ring: e.ring, side: e.side, pitch: e.pitch, points: points.map((p) => p.map(round6) as Vec3) });
  }
  roof.faces.sort((p, q) => p.ring - q.ring || p.side - q.side);
  const gables = new Set(edges.filter((e) => e.gable).map((e) => e.index));
  const segments = new Map<string, { faces: Set<number>; kind: string }>();
  for (const [face, pieces] of byFace) {
    for (const [k, kind] of pieces) {
      let item = segments.get(k);
      if (!item) {
        item = { faces: new Set(), kind };
        segments.set(k, item);
      }
      item.faces.add(face);
      if (kind !== "ridge") item.kind = kind;
    }
  }
  for (const e of edges) {
    if (e.gable) continue;
    const k = key(start.get(e.index)!, end.get(e.index)!);
    const item = segments.get(k) ?? { faces: new Set([e.index]), kind: "eave" };
    item.kind = "eave";
    segments.set(k, item);
  }
  const keys = [...segments.keys()].map((k) => k.split("|").map(Number) as [number, number]).sort((p, q) => p[0] - q[0] || p[1] - q[1]);
  for (const [a, b] of keys) {
    const item = segments.get(`${a}|${b}`)!;
    const faces = [...item.faces].filter((f) => !gables.has(f));
    if (!faces.length) continue;
    let type: SkeletonLine["type"];
    if (item.kind === "eave") type = "eave";
    else if (faces.length === 1) type = "rake";
    else if (Math.abs(nodes[a]![2] - nodes[b]![2]) < 1e-6) type = "ridge";
    else type = item.kind === "reflex" ? "valley" : "hip";
    const face = edges[Math.min(...faces)]!;
    const r3 = (p: Vec3): Vec3 => [round6(p[0]), round6(p[1]), round6(height(face, p))];
    roof.lines.push({ a: r3(nodes[a]!), b: r3(nodes[b]!), type });
  }
  return roof;
}

function walk(nodes: Vec3[], pieces: Map<string, string>, start: number, end: number): number[] | null {
  const around = new Map<number, number[]>();
  for (const k of pieces.keys()) {
    const [a, b] = k.split("|").map(Number) as [number, number];
    around.set(a, [...(around.get(a) ?? []), b]);
    around.set(b, [...(around.get(b) ?? []), a]);
  }
  const ring = [start, end];
  let previous = start;
  let current = end;
  let guard = 0;
  while (current !== start && guard++ < 10000) {
    const options = (around.get(current) ?? []).filter((n) => n !== previous);
    if (!options.length) return null;
    if (options.length > 1) {
      const back = Math.atan2(nodes[previous]![1] - nodes[current]![1], nodes[previous]![0] - nodes[current]![0]);
      const turn = (n: number) => {
        const angle = Math.atan2(nodes[n]![1] - nodes[current]![1], nodes[n]![0] - nodes[current]![0]);
        return mod(back - angle, 2 * Math.PI) || 2 * Math.PI;
      };
      options.sort((p, q) => turn(p) - turn(q));
    }
    const next = options[0]!;
    if (next === start) break;
    if (ring.includes(next)) return null;
    ring.push(next);
    previous = current;
    current = next;
  }
  return ring.length >= 3 ? ring : null;
}

// ------------------------------------------------------------------------------------------- presets
export type FootprintPreset = "hip" | "gable" | "gable-across";

function isRectangle(points: Vec2[], toleranceDeg = 4): boolean {
  if (points.length !== 4) return false;
  for (let i = 0; i < 4; i++) {
    const a = sub(points[(i + 3) % 4]!, points[i]!);
    const b = sub(points[(i + 1) % 4]!, points[i]!);
    if (Math.abs(dot(a, b)) / (norm(a) * norm(b) || 1) > Math.sin((toleranceDeg * Math.PI) / 180)) return false;
  }
  return true;
}

/** Edge modes of the chooser's shapes on any footprint (as the server's `preset_specs`): `hip` every side, `gable`
 * gable ends on a rectangle's short sides (another outline: every short side between two outside corners),
 * `gable-across` a rectangle's long sides. */
export function presetSpecs(points: Vec2[], preset: FootprintPreset, pitch: number): EdgeSpec[] {
  const n = points.length;
  const hip = points.map(() => ({ mode: "hip" as EdgeMode, pitch }));
  if (preset === "hip") return hip;
  const ccw = ringArea(points) > 0;
  const lengths = points.map((p, i) => norm(sub(points[(i + 1) % n]!, p)));
  if (n === 4 && isRectangle(points)) {
    const longFirst = lengths[0]! >= lengths[1]!;
    const gableSides = (preset === "gable") === longFirst ? [1, 3] : [0, 2];
    return points.map((_, i) => ({ mode: gableSides.includes(i) ? "gable" : "hip", pitch }));
  }
  if (preset !== "gable") return hip;
  const isConvex = (i: number) => {
    const a = points[mod(i - 1, n)]!;
    const b = points[i]!;
    const c = points[mod(i + 1, n)]!;
    const turn = cross(sub(b, a), sub(c, b));
    return ccw ? turn > 0 : turn < 0;
  };
  return points.map((_, i) => {
    const before = lengths[mod(i - 1, n)]!;
    const after = lengths[mod(i + 1, n)]!;
    const short = lengths[i]! <= Math.min(before, after) + 1e-6 && lengths[i]! < Math.max(before, after) - 1e-6;
    return { mode: short && isConvex(i) && isConvex(mod(i + 1, n)) ? "gable" : "hip", pitch };
  });
}

/** The roof as the shared fixtures write it. */
export function roofAsDict(roof: SkeletonRoof) {
  return {
    faces: roof.faces.map((f) => ({ ring: f.ring, side: f.side, pitch_deg: f.pitch, points: f.points })),
    lines: roof.lines.map((l) => ({ a: l.a, b: l.b, type: l.type })),
  };
}
